import { Cloud, Book } from '@readcloud/data';
import { CloudWithBooks } from '../types';
import { HomeSettings } from '@readcloud/state';

/**
 * Groups Books by clouds
 * Hides any clouds required by settings
 * Sorts clouds based on settings + order
 *
 * @param books
 * @param clouds
 * @param settings
 */
export const groupFilterSortClouds = (
  books: Book[],
  clouds: Cloud[],
  settings: HomeSettings
): CloudWithBooks[] => {
  return (
    clouds

      // 1. Attach books

      .map((c, i) => ({
        ...c,
        bookData:
          books
            ?.filter((b) => c.books && c.books.some((y) => y === b.id))
            .slice()
            .sort((a, b) =>
              c.customBookSortOrder
                ? c.books.indexOf(a.id || '') - c.books.indexOf(b.id || '')
                : Date.parse(b.created) - Date.parse(a.created)
            ) || [],
        order: 0,
      }))

      // 2. Inject Interactive Cloud if we have to

      .concat(
        settings.myInteractiveLoginsPosition !== 'hide'
          ? makeInterativeCloud(books)
          : []
      )

      // 3. Position My Purchases Cloud if we have to

      ?.filter(
        (c) =>
          settings.myPurchasesCloudPosition !== 'hide' ||
          c.name.toLowerCase() !== 'my purchases'
      )

      // 4. Remove interactives from My Purcahses cloud

      ?.map((c) =>
        c.name.toLowerCase() === 'my purchases'
          ? {
              ...c,
              bookData: c.bookData.filter(isNormalBook),
            }
          : c
      )

      // 5. Hide my Interactive if we have to

      ?.filter(
        (c) =>
          settings.myInteractiveLoginsPosition !== 'hide' ||
          c.name.toLowerCase() !== 'my interactive logins'
      )

      // 6. Hide empty clouds if we have to

      ?.filter((c) => !settings.hideEmptyClouds || c.bookData?.length > 0)

      // 7. Order by cloud name

      ?.sort((a, b) => a.name.localeCompare(b.name))

      // 8. Determine order for clouds

      ?.map((c, i) => ({ ...c, order: getOrder(c, i, settings) }))

      // 9. Slice to copy array

      ?.slice()

      // 10. Resort to factor in filters / changes

      ?.sort((a, b) => a.order - b.order)
  );
};
/**
 * Helper function to determine sort order for clouds
 *
 * @param cloud
 * @param index
 * @param settings
 */
const getOrder = (
  cloud: Cloud,
  index: number,
  settings: HomeSettings
): number => {
  const c = cloud.name.toLowerCase();
  //
  // My Purchases cloud positioning
  //
  if (c === 'my purchases') {
    return settings.myPurchasesCloudPosition === 'top'
      ? -8
      : settings.myPurchasesCloudPosition === 'bottom'
      ? 9998
      : index;
  }
  //
  // My Interactive Logins positioning
  //
  else if (c === 'my interactive logins') {
    return settings.myInteractiveLoginsPosition === 'top'
      ? -9
      : settings.myInteractiveLoginsPosition === 'bottom'
      ? 9997
      : index;
  }

  return index;
};

/**
 * Helper function for determining if book is a normal / readable book
 * @param book
 */
const isNormalBook = (book: Book) => {
  return !isInteractiveBook(book);
};
/**
 * Helper to make an interactive cloud from books
 * @param books
 */
const makeInterativeCloud = (books: Book[]): CloudWithBooks => {
  return {
    id: 'int',
    name: 'My Interactive Logins',
    books: [],
    order: 0,
    bookData: books
      ?.filter(isInteractiveBook)
      .slice()
      .sort((a, b) => b.id.localeCompare(a.id)),
    description: 'My Interactive Logins',
    institution: '',
    type: 'System',
    members: [],
    admins: [],
    addBookAdminOnly: true,
    persistent: true,
    created: new Date().toString(),
    updated: new Date().toString(),
    deleted: false,
    // TODO: Adding fields with blank values here because myInteractiveCloud doesn't fit the mould.
    archivedFor: [],
    axcelerateId: '',
    customBookSortOrder: false,
    extConfig: {},
    institutions: [],
    resellers: [],
    subject: '',
    yearLevel: '',
  };
};

/**
 * Helper function for determining if book is an interactive tile
 * @param book
 */
const isInteractiveBook = (book: Book) => {
  return !!book.intLoginData || (book.isbn && book.isbn.indexOf('RC_INT') > -1);
};
